import {
  MEETING_BOOKING_ACTION,
  MEETING_BOOKING_SESSION_RECOMMEND_EVENT_KIND,
  MEETING_BOOKING_SESSION_RECOMMEND_EVENT_NAME,
  MEETING_BOOKING_SUBJECT_TYPE
} from '../constants';
import type {
  MeetingOrderPointHubCollectDraft,
  NormalizedMeetingBookingSessionRecommendEvent,
  PointHubValidationResult
} from '../types';

type UnknownRecord = Record<string, unknown>;

const SESSION_RESULTS = new Set(['success', 'cancelled', 'failed']);
const BOOKING_METHODS = new Set([
  'ai_direct',
  'ai_then_manual',
  'manual_form',
  'no_ai_cancelled',
  'ai_cancelled',
  'ai_then_manual_cancelled'
]);
const PLATFORMS = new Set(['web', 'h5', 'miniprogram']);
const FIELD_GROUPS = new Set(['content', 'participant', 'location', 'online', 'setting', 'attachment', 'permission', 'service']);
const VALUE_TYPES = new Set(['string', 'integer', 'number', 'boolean', 'object', 'array_string', 'array_object', 'null']);
const ARRAY_FIELD_NAMES = new Set(['participants', 'meetingOnline', 'meetingFile', 'serviceItems', 'restrictiveRules']);

const isRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim().length > 0;
};

const isNonNegativeNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
};

const isNonNegativeInteger = (value: unknown): value is number => {
  return isNonNegativeNumber(value) && Number.isInteger(value);
};

const pushIf = (errors: string[], invalid: boolean, field: string): void => {
  if (invalid) errors.push(field);
};

const validateListItem = (value: unknown, path: string, errors: string[]): void => {
  if (!isRecord(value)) {
    errors.push(path);
    return;
  }
  pushIf(errors, !isNonEmptyString(value.label), `${path}.label`);
  pushIf(
    errors,
    !isNonEmptyString(value.value) && typeof value.value !== 'number' && typeof value.value !== 'boolean',
    `${path}.value`
  );
  pushIf(errors, !ARRAY_FIELD_NAMES.has(String(value.name)), `${path}.name`);
};

const validateRecordArray = (
  value: unknown,
  path: string,
  errors: string[],
  validateItem: (item: UnknownRecord, itemPath: string, itemErrors: string[]) => void
): void => {
  if (!Array.isArray(value)) {
    errors.push(path);
    return;
  }
  value.forEach((item, index) => {
    const itemPath = `${path}[${index}]`;
    if (!isRecord(item)) errors.push(itemPath);
    else validateItem(item, itemPath, errors);
  });
};

const validateSnapshot = (value: unknown, path: string, errors: string[]): void => {
  if (!isRecord(value)) {
    errors.push(path);
    return;
  }
  if (value.participants !== undefined) {
    validateRecordArray(value.participants, `${path}.participants`, errors, (item, itemPath) => {
      validateListItem(item, itemPath, errors);
      pushIf(errors, item.name !== 'participants', `${itemPath}.name`);
      pushIf(errors, !isNonEmptyString(item.participant_id), `${itemPath}.participant_id`);
      pushIf(errors, !isNonEmptyString(item.participant_name), `${itemPath}.participant_name`);
      pushIf(errors, !isNonEmptyString(item.participant_role), `${itemPath}.participant_role`);
    });
  }
  if (value.meeting_address !== undefined) {
    const addressPath = `${path}.meeting_address`;
    if (!isRecord(value.meeting_address)) errors.push(addressPath);
    else {
      pushIf(errors, !isNonEmptyString(value.meeting_address.room_id), `${addressPath}.room_id`);
      pushIf(errors, !isNonEmptyString(value.meeting_address.room_name), `${addressPath}.room_name`);
      pushIf(errors, !isNonEmptyString(value.meeting_address.address_type), `${addressPath}.address_type`);
    }
  }
  if (value.meeting_online !== undefined) {
    validateRecordArray(value.meeting_online, `${path}.meeting_online`, errors, (item, itemPath) => {
      validateListItem(item, itemPath, errors);
      pushIf(errors, item.name !== 'meetingOnline', `${itemPath}.name`);
      pushIf(errors, !isNonNegativeInteger(item.online_type), `${itemPath}.online_type`);
      pushIf(errors, !isNonEmptyString(item.online_name), `${itemPath}.online_name`);
      ['auto_mute', 'auto_record', 'meeting_password'].forEach((field) => {
        pushIf(errors, typeof item[field] !== 'boolean', `${itemPath}.${field}`);
      });
    });
  }
  if (value.meeting_remind !== undefined) {
    const remindPath = `${path}.meeting_remind`;
    if (!isRecord(value.meeting_remind)) errors.push(remindPath);
    else {
      pushIf(errors, typeof value.meeting_remind.remind_enabled !== 'boolean', `${remindPath}.remind_enabled`);
      pushIf(errors, !isNonNegativeInteger(value.meeting_remind.before_minutes), `${remindPath}.before_minutes`);
    }
  }
  if (value.meeting_file !== undefined) {
    validateRecordArray(value.meeting_file, `${path}.meeting_file`, errors, (item, itemPath) => {
      validateListItem(item, itemPath, errors);
      pushIf(errors, item.name !== 'meetingFile', `${itemPath}.name`);
      pushIf(errors, !isNonEmptyString(item.file_id), `${itemPath}.file_id`);
      pushIf(errors, !isNonEmptyString(item.file_name), `${itemPath}.file_name`);
      pushIf(errors, !isNonNegativeNumber(item.file_size_mb), `${itemPath}.file_size_mb`);
    });
  }
  if (value.auto_release_room !== undefined) {
    const releasePath = `${path}.auto_release_room`;
    if (!isRecord(value.auto_release_room)) errors.push(releasePath);
    else {
      pushIf(errors, typeof value.auto_release_room.enabled !== 'boolean', `${releasePath}.enabled`);
      pushIf(errors, !isNonNegativeInteger(value.auto_release_room.after_minutes), `${releasePath}.after_minutes`);
    }
  }
  if (value.service_items !== undefined) {
    validateRecordArray(value.service_items, `${path}.service_items`, errors, (item, itemPath) => {
      validateListItem(item, itemPath, errors);
      pushIf(errors, item.name !== 'serviceItems', `${itemPath}.name`);
    });
  }
  if (value.restrictive_rules !== undefined) {
    validateRecordArray(value.restrictive_rules, `${path}.restrictive_rules`, errors, (item, itemPath) => {
      validateListItem(item, itemPath, errors);
      pushIf(errors, item.name !== 'restrictiveRules', `${itemPath}.name`);
      pushIf(errors, !isNonEmptyString(item.rule_id), `${itemPath}.rule_id`);
      pushIf(errors, !isNonEmptyString(item.rule_name), `${itemPath}.rule_name`);
      pushIf(errors, typeof item.rule_hit !== 'boolean', `${itemPath}.rule_hit`);
    });
  }
};

export const validateMeetingBookingCollectDraft = (
  value: unknown
): PointHubValidationResult<MeetingOrderPointHubCollectDraft> => {
  const errors: string[] = [];
  if (!isRecord(value)) return { valid: false, errors: ['draft'] };

  pushIf(errors, value.eventName !== MEETING_BOOKING_SESSION_RECOMMEND_EVENT_NAME, 'eventName');
  pushIf(errors, value.eventKind !== MEETING_BOOKING_SESSION_RECOMMEND_EVENT_KIND, 'eventKind');
  pushIf(errors, !isNonEmptyString(value.eventId), 'eventId');
  pushIf(errors, !isNonEmptyString(value.occurredAt) || Number.isNaN(Date.parse(String(value.occurredAt))), 'occurredAt');
  pushIf(errors, !isNonEmptyString(value.sessionId), 'sessionId');
  pushIf(errors, value.subjectType !== MEETING_BOOKING_SUBJECT_TYPE, 'subjectType');
  pushIf(errors, value.action !== MEETING_BOOKING_ACTION, 'action');
  pushIf(errors, !SESSION_RESULTS.has(String(value.result)), 'result');
  pushIf(errors, !isNonNegativeInteger(value.durationMs), 'durationMs');
  pushIf(errors, !isRecord(value.properties), 'properties');
  pushIf(errors, !Array.isArray(value.diagnostics), 'diagnostics');

  return {
    valid: errors.length === 0,
    value: errors.length === 0 ? (value as unknown as MeetingOrderPointHubCollectDraft) : undefined,
    errors
  };
};

export const validateMeetingBookingSessionRecommend = (
  event: NormalizedMeetingBookingSessionRecommendEvent
): PointHubValidationResult<NormalizedMeetingBookingSessionRecommendEvent> => {
  const errors: string[] = [];
  const properties = event.properties;

  pushIf(errors, event.eventName !== MEETING_BOOKING_SESSION_RECOMMEND_EVENT_NAME, 'eventName');
  pushIf(errors, !isNonEmptyString(event.eventId), 'eventId');
  pushIf(errors, !isNonEmptyString(event.sessionId), 'sessionId');
  pushIf(errors, event.subjectType !== MEETING_BOOKING_SUBJECT_TYPE, 'subjectType');
  pushIf(errors, event.action !== MEETING_BOOKING_ACTION, 'action');
  pushIf(errors, !SESSION_RESULTS.has(event.result), 'result');
  pushIf(errors, event.result === 'success' && !isNonEmptyString(event.subjectId), 'subjectId');
  pushIf(errors, !isNonNegativeInteger(event.durationMs), 'durationMs');

  const occurredAt = Date.parse(event.occurredAt);
  pushIf(errors, Number.isNaN(occurredAt), 'occurredAt');
  pushIf(errors, !isNonEmptyString(properties.flow_id), 'properties.flow_id');
  pushIf(errors, properties.flow_id !== event.sessionId, 'properties.flow_id_consistency');
  pushIf(errors, !PLATFORMS.has(properties.platform), 'properties.platform');
  pushIf(errors, properties.entry_source !== 'schedule_entry', 'properties.entry_source');
  pushIf(errors, properties.booking_scene !== 'meeting_booking', 'properties.booking_scene');
  pushIf(errors, properties.session_result !== event.result, 'properties.session_result_consistency');
  pushIf(errors, !BOOKING_METHODS.has(properties.booking_method), 'properties.booking_method');
  pushIf(errors, typeof properties.ai_used !== 'boolean', 'properties.ai_used');
  pushIf(errors, typeof properties.manual_edit_after_ai !== 'boolean', 'properties.manual_edit_after_ai');

  ['started_at', 'closed_at', 'duration_ms', 'active_duration_ms', 'idle_duration_ms'].forEach((field) => {
    pushIf(errors, !isNonNegativeInteger(properties[field as keyof typeof properties]), `properties.${field}`);
  });
  if (properties.submitted_at !== undefined) {
    pushIf(errors, !isNonNegativeInteger(properties.submitted_at), 'properties.submitted_at');
  }
  pushIf(errors, properties.closed_at < properties.started_at, 'properties.closed_at_range');
  pushIf(
    errors,
    properties.submitted_at !== undefined &&
      (properties.submitted_at < properties.started_at || properties.submitted_at > properties.closed_at),
    'properties.submitted_at_range'
  );
  pushIf(errors, properties.duration_ms !== event.durationMs, 'properties.duration_ms_consistency');
  pushIf(errors, properties.active_duration_ms > properties.duration_ms, 'properties.active_duration_ms_range');
  pushIf(errors, properties.idle_duration_ms > properties.duration_ms, 'properties.idle_duration_ms_range');
  pushIf(
    errors,
    properties.active_duration_ms + properties.idle_duration_ms !== properties.duration_ms,
    'properties.duration_parts_consistency'
  );
  pushIf(
    errors,
    !Number.isNaN(occurredAt) && Math.abs(occurredAt - properties.closed_at) > 1,
    'properties.closed_at_consistency'
  );

  [
    'ai_user_turn_count',
    'ai_recommendation_apply_count',
    'manual_edit_count',
    'manual_edited_field_count'
  ].forEach((field) => {
    pushIf(errors, !isNonNegativeInteger(properties[field as keyof typeof properties]), `properties.${field}`);
  });
  pushIf(errors, !Array.isArray(properties.manual_edited_fields), 'properties.manual_edited_fields');
  pushIf(errors, !Array.isArray(properties.field_edit_detail), 'properties.field_edit_detail');
  pushIf(errors, !isRecord(properties.final_form_snapshot), 'properties.final_form_snapshot');
  pushIf(errors, !Array.isArray(properties.ai_modified_fields_summary), 'properties.ai_modified_fields_summary');
  pushIf(errors, !Array.isArray(properties.conversation_detail), 'properties.conversation_detail');

  if (Array.isArray(properties.manual_edited_fields)) {
    pushIf(
      errors,
      properties.manual_edited_field_count !== properties.manual_edited_fields.length,
      'properties.manual_edited_field_count_consistency'
    );
  }
  if (Array.isArray(properties.field_edit_detail)) {
    pushIf(
      errors,
      properties.manual_edit_count !== properties.field_edit_detail.length,
      'properties.manual_edit_count_consistency'
    );
    properties.field_edit_detail.forEach((detail, index) => {
      pushIf(errors, detail.edit_index !== index + 1, `properties.field_edit_detail[${index}].edit_index`);
    });
  }
  if (Array.isArray(properties.conversation_detail)) {
    pushIf(
      errors,
      properties.ai_user_turn_count !== properties.conversation_detail.length,
      'properties.ai_user_turn_count_consistency'
    );
  }

  validateSnapshot(properties.final_form_snapshot, 'properties.final_form_snapshot', errors);
  if (properties.latest_ai_snapshot !== undefined) {
    validateSnapshot(properties.latest_ai_snapshot, 'properties.latest_ai_snapshot', errors);
  }
  validateRecordArray(
    properties.manual_edited_fields,
    'properties.manual_edited_fields',
    errors,
    (item, itemPath) => {
      pushIf(errors, !isNonEmptyString(item.field_key), `${itemPath}.field_key`);
      pushIf(errors, !isNonEmptyString(item.field_name), `${itemPath}.field_name`);
      pushIf(errors, !FIELD_GROUPS.has(String(item.field_group)), `${itemPath}.field_group`);
      pushIf(errors, !isNonNegativeInteger(item.edit_count), `${itemPath}.edit_count`);
    }
  );
  validateRecordArray(properties.field_edit_detail, 'properties.field_edit_detail', errors, (item, itemPath) => {
    pushIf(errors, !isNonEmptyString(item.edit_id), `${itemPath}.edit_id`);
    pushIf(errors, !isNonNegativeInteger(item.edit_index) || item.edit_index === 0, `${itemPath}.edit_index`);
    pushIf(errors, !isNonNegativeInteger(item.occurred_at), `${itemPath}.occurred_at`);
    pushIf(errors, !isNonEmptyString(item.field_key), `${itemPath}.field_key`);
    pushIf(errors, !isNonEmptyString(item.field_name), `${itemPath}.field_name`);
    pushIf(errors, !FIELD_GROUPS.has(String(item.field_group)), `${itemPath}.field_group`);
    pushIf(errors, typeof item.after_ai_used !== 'boolean', `${itemPath}.after_ai_used`);
    pushIf(errors, typeof item.changed_from_ai_prefill !== 'boolean', `${itemPath}.changed_from_ai_prefill`);
    pushIf(errors, !VALUE_TYPES.has(String(item.old_value_type)), `${itemPath}.old_value_type`);
    pushIf(errors, !VALUE_TYPES.has(String(item.new_value_type)), `${itemPath}.new_value_type`);
    if (item.old_value_list !== undefined) {
      validateRecordArray(item.old_value_list, `${itemPath}.old_value_list`, errors, validateListItem);
    }
    if (item.new_value_list !== undefined) {
      validateRecordArray(item.new_value_list, `${itemPath}.new_value_list`, errors, validateListItem);
    }
  });
  validateRecordArray(
    properties.ai_modified_fields_summary,
    'properties.ai_modified_fields_summary',
    errors,
    (item, itemPath) => {
      pushIf(errors, !isNonEmptyString(item.field_key), `${itemPath}.field_key`);
      pushIf(errors, !isNonEmptyString(item.field_name), `${itemPath}.field_name`);
      pushIf(errors, !FIELD_GROUPS.has(String(item.field_group)), `${itemPath}.field_group`);
      pushIf(errors, typeof item.changed !== 'boolean', `${itemPath}.changed`);
      if (item.ai_value_list !== undefined) {
        validateRecordArray(item.ai_value_list, `${itemPath}.ai_value_list`, errors, validateListItem);
      }
      if (item.final_value_list !== undefined) {
        validateRecordArray(item.final_value_list, `${itemPath}.final_value_list`, errors, validateListItem);
      }
    }
  );
  if (properties.conversation_summary !== undefined) {
    const summary = properties.conversation_summary as unknown;
    if (!isRecord(summary)) errors.push('properties.conversation_summary');
    else {
      ['user_turn_count', 'ai_turn_count', 'first_ai_used_at', 'last_ai_used_at'].forEach((field) => {
        pushIf(errors, !isNonNegativeInteger(summary[field]), `properties.conversation_summary.${field}`);
      });
    }
  }
  validateRecordArray(properties.conversation_detail, 'properties.conversation_detail', errors, (item, itemPath) => {
    pushIf(errors, !isNonNegativeInteger(item.turn_index) || item.turn_index === 0, `${itemPath}.turn_index`);
    pushIf(errors, !isNonEmptyString(item.message_id), `${itemPath}.message_id`);
    pushIf(errors, !isNonNegativeInteger(item.occurred_at), `${itemPath}.occurred_at`);
    pushIf(errors, typeof item.message_text !== 'string', `${itemPath}.message_text`);
    pushIf(errors, !isNonNegativeInteger(item.message_length), `${itemPath}.message_length`);
    pushIf(errors, !isNonEmptyString(item.intent_type), `${itemPath}.intent_type`);
    pushIf(errors, typeof item.contains_time_expression !== 'boolean', `${itemPath}.contains_time_expression`);
    pushIf(errors, typeof item.contains_participant_expression !== 'boolean', `${itemPath}.contains_participant_expression`);
    if (typeof item.message_text === 'string' && isNonNegativeInteger(item.message_length)) {
      pushIf(errors, Array.from(item.message_text).length !== item.message_length, `${itemPath}.message_length_consistency`);
    }
  });

  return {
    valid: errors.length === 0,
    value: errors.length === 0 ? event : undefined,
    errors: Array.from(new Set(errors))
  };
};
